<template>
  <el-dialog
    v-model="visible"
    class="base-dialog"
    :width="width"
    draggable
    align-center
    append-to-body
    :header-class="showHeader ? '' : 'hidden'"
    :fullscreen="fullScreen"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
  >
    <template #header>
      <slot
        v-if="showHeader"
        name="header"
      >
        <div class="flex items-center justify-between w-full">
          <span class="text-lg font-medium">
            {{ title }}
          </span>
          <button
            v-if="showClose"
            type="button"
            @click="cancel"
          >
            <Icon
              icon="sky:ic-close"
              class="size-6 text-text-2 hover:text-text-1 transition-colors"
            />
          </button>
        </div>
      </slot>
    </template>
    <div
      class="base-dialog-body overflow-y-auto scrollbar-none"
      :style="bodySizeStyle"
    >
      <slot />
    </div>
    <template #footer>
      <slot
        v-if="showFooter"
        name="footer"
      >
        <div class="flex justify-end pt-[20px]">
          <el-button
            type="default"
            class="el-button--default min-w-[120px]"
            size="large"
            v-bind="cancelProps"
            @click="cancel"
          >
            {{ cancelText }}
          </el-button>
          <el-button
            type="primary"
            class="el-button--primary min-w-[120px]"
            size="large"
            v-bind="confirmProps"
            @click="confirm"
          >
            {{ confirmText }}
          </el-button>
        </div>
      </slot>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, CSSProperties } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ButtonProps } from 'element-plus'

const { t } = useI18n()

type Props = {
  title?: string // 标题
  size?: 'small' | 'medium' | 'large' | 'extra-large' // 宽度预设值
  width?: string | number // 宽度，设置此值会覆盖size
  cancelButtonText?: string // 取消按钮文本
  confirmButtonText?: string // 确认按钮文本
  confirmProps?: ButtonProps // 确认按钮props
  cancelProps?: ButtonProps // 取消按钮props
  showClose?: boolean // 是否显示关闭按钮
  showHeader?: boolean // 是否显示头部
  showFooter?: boolean // 是否显示底部按钮组
  fullScreen?: boolean // 是否全屏
  isFixedHeight?: boolean // 是否固定高度，80vh
  bodyStyle?: CSSProperties //  body样式，用于自定义设置body高度
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  size: 'medium',
  cancelButtonText: '',
  confirmButtonText: '',
  confirmProps: () => ({}),
  cancelProps: () => ({}),
  showClose: true,
  showHeader: true,
  showFooter: true,
  fullScreen: false,
  isFixedHeight: false,
  bodyStyle: () => ({}),
})

type Emits = {
  confirm: []
  cancel: []
}

const emit = defineEmits<Emits>()

const visible = defineModel<boolean>({ default: false })

const width = computed(() => {
  if (props.width) {
    return props.width
  }
  if (props.size === 'small') {
    return (6 / 20 * 100) + '%'
  }
  if (props.size === 'medium') {
    return (8 / 20 * 100) + '%'
  }
  if (props.size === 'large') {
    return (12 / 20 * 100) + '%'
  }
  return (14 / 20 * 100) + '%'
})

const HEADER_HEIGHT = 48
const FOOTER_HEIGHT = 64
const PADDING = 24

const bodySizeStyle = computed(() => {
  const headerOffset = props.showHeader ? HEADER_HEIGHT : 0
  const footerOffset = props.showFooter ? FOOTER_HEIGHT : 0
  const totalOffset = headerOffset + footerOffset + PADDING * 2
  if (Object.keys(props.bodyStyle).length > 0) {
    return props.bodyStyle
  }
  if (props.fullScreen) {
    return {
      height: `calc(100vh - ${totalOffset}px)`,
    }
  }
  if (props.isFixedHeight) {
    return {
      height: `calc(80vh - ${totalOffset}px)`,
    }
  }
  return {
    maxHeight: totalOffset ? `calc(80vh - ${totalOffset}px)` : '80vh',
  }
})

const cancelText = computed(() => {
  return props.cancelButtonText || t('common.cancel')
})

const confirmText = computed(() => {
  return props.confirmButtonText || t('common.confirm')
})

const confirm = () => {
  emit('confirm')
}

const cancel = () => {
  visible.value = false
  emit('cancel')
}
</script>

<style scoped>
.base-dialog :deep(.el-dialog) {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.base-dialog :deep(.el-dialog__body) {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.base-dialog-body {
  overflow-y: auto;
  min-height: 150px;
}
</style>
